import React, { useState } from "react";

import { Tab } from "@headlessui/react";
import PropTypes from "prop-types";

import { BankingOption } from "common/contexts";

import { FaCheckCircle } from "components/atoms/Icon/fa/FaCheckCircle";
import { FaTimesCircle } from "components/atoms/Icon/fa/FaTimesCircle";
import Title from "components/atoms/Title";
import BankingMultiSelect from "components/molecules/BankingMultiSelect";
import BankingTable from "components/molecules/BankingTable";
import TabListNew from "components/molecules/TabListNew/index";

const BankCard = ({ bank, thead }) => {
  return (
    <div
      className="mt-2 rounded-3xl bg-white py-6 px-5"
      style={{ boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)" }}
    >
      {bank.contact_us ? (
        <>
          <p className="mb-5 text-xl font-bold tracking-[0.15px] text-bbc-black">
            {bank.name}
          </p>
          <p>Contact us for details</p>
        </>
      ) : (
        <>
          <p className="mb-5 text-xl font-bold tracking-[0.15px] text-bbc-black">
            {bank.name}
          </p>
          <div className="mb-2.5 flex items-center">
            {bank.online_banking || bank.online_registration ? (
              <FaCheckCircle className="mr-2.5 text-bbc-brand-600" size={16} />
            ) : (
              <FaTimesCircle className="mr-2.5 text-bbc-red-400" size={16} />
            )}
            {thead[2]}
          </div>
          <div className="mb-2.5 flex items-center">
            {bank.personal_visit || bank.multiple_account ? (
              <FaCheckCircle className="mr-2.5 text-bbc-brand-600" size={16} />
            ) : (
              <FaTimesCircle className="mr-2.5 text-bbc-red-400" size={16} />
            )}
            {thead[3]}
          </div>
          <div className="mb-2.5 flex items-center">
            {bank.credit_card || bank.support_transactions ? (
              <FaCheckCircle className="mr-2.5 text-bbc-brand-600" size={16} />
            ) : (
              <FaTimesCircle className="mr-2.5 text-bbc-red-400" size={16} />
            )}
            {thead[4]}
          </div>
          <div
            className={`flex items-center ${
              (bank.initial_deposit || bank.other_features) && "mb-5 pb-4"
            }  `}
          >
            {bank.debit_card || bank.physical_card ? (
              <FaCheckCircle className="mr-2.5 text-bbc-brand-600" size={16} />
            ) : (
              <FaTimesCircle className="mr-2.5 text-bbc-red-400" size={16} />
            )}
            {thead[5]}
          </div>
          {(bank.initial_deposit || bank.other_features) && (
            <>
              <p className="mb-3.5 border-t border-t-[#797979]">{thead[6]}</p>
              <p>{bank.initial_deposit || bank.other_features}</p>
            </>
          )}
        </>
      )}
    </div>
  );
};

export const BankMobile = ({ data, thead }) => {
  return (
    <>
      {data.length < 2 ? (
        data[0].banks.map((bank, bankIdx) => (
          <BankCard key={bankIdx} bank={bank} thead={thead} />
        ))
      ) : (
        <TabListNew
          data={data.map((item) => item.name)}
          tabListClass={{
            length: "w-full",
            border: "border-0",
            display:
              data.length > 2 ? "flex justify-start" : "flex justify-center",
            padding: "px-2 py-1.5",
            othersClass: "whitespace-nowrap overflow-x-auto",
          }}
          tabClass={{
            tabColor: "text-bbc-brand-800 border-transparent",
            padding: "px-6 py-2",
          }}
        >
          <Tab.Panels className="mt-6">
            {data.map((country, idx) => (
              <Tab.Panel
                className="focus:outline-none focus:outline-0 focus:ring-0"
                as="div"
                key={idx}
              >
                {country.banks.map((bank, bankIdx) => (
                  <BankCard key={bankIdx} bank={bank} thead={thead} />
                ))}
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </TabListNew>
      )}
    </>
  );
};

const traditionalThead = [
  "Country",
  "Bank",
  "Online Banking",
  "Personal Visit",
  "Credit Card",
  "Debit Card",
  "Initial Deposit",
];
const emiThead = [
  "Country",
  "EMI",
  "Online registration",
  "Multiple currency business account",
  "Support international transactions",
  "Physical card",
  "Other features",
];
const BankingOptions = ({
  dataTraditional,
  dataEMI,
  title,
  description,
  noteTraditional,
  noteEMI,
  hideNote,
  noSelect,
  tabArray,
}) => {
  const [dataTable, setDataTable] = useState(dataTraditional);

  return (
    <>
      <div className="flex justify-center ">
        <TabListNew
          data={tabArray}
          tabListClass={
            tabArray.length > 1
              ? { background: "bg-white" }
              : { background: "bg-white", display: "hidden" }
          }
        >
          {tabArray.includes("Traditional Bank") && (
            <Tab.Panel
              className="focus:outline-none focus:outline-0 focus:ring-0"
              as="div"
              key={"traditional-bank"}
            >
              <Title className="my-6 pb-0 text-center text-2xl font-semibold leading-[44px] text-bbc-brand-800 md:my-8 md:text-3xl">
                {title}
              </Title>
              {description && (
                <p className="mb-10 text-center md:mb-8 md:text-left">
                  {description}
                </p>
              )}
              <BankingOption.Provider
                value={{ data: dataTraditional, dataTable, setDataTable }}
              >
                {!noSelect && <BankingMultiSelect />}
                <div className="hidden md:block">
                  <BankingTable
                    hideNote={hideNote}
                    data={dataTable}
                    className="mt-4"
                    thead={traditionalThead}
                    description={noteTraditional}
                  />
                </div>
                <div className="mt-6 block md:hidden ">
                  <BankMobile data={dataTable} thead={traditionalThead} />
                </div>
              </BankingOption.Provider>
            </Tab.Panel>
          )}
          {tabArray.includes("EMI") && (
            <Tab.Panel
              className="focus:outline-none focus:outline-0 focus:ring-0"
              as="div"
              key={"electronic-money-institutions"}
            >
              <Title className="my-10 pb-0 text-center text-2xl font-semibold leading-[44px] text-bbc-brand-800 md:my-8 md:text-3xl">
                Electronic Money Institutions
              </Title>
              <p className="text-center md:text-left">
                In case you struggle in opening your business account in
                traditional banks, there is an alternative solution - Electronic
                Money Institution (EMI). EMIs are financial institutions that
                provide similar bank-like services and payment-related functions
                such as e-wallets, payments cards, or IBAN accounts. Here are
                the top EMIs you can consider.
              </p>
              <div className="hidden md:block">
                <BankingTable
                  hideNote={hideNote}
                  data={dataEMI}
                  emi={true}
                  thead={emiThead}
                  className="mt-4"
                  description={noteEMI}
                />
              </div>
              <div className="mt-6  block md:hidden ">
                <BankMobile data={dataEMI} thead={emiThead} />
              </div>
            </Tab.Panel>
          )}
        </TabListNew>
      </div>
    </>
  );
};

export default BankingOptions;

BankingOptions.propTypes = {
  data: PropTypes.array,
  emi: PropTypes.bool,
};
BankingOptions.defaultProps = {
  data: [],
  tabArray: ["Traditional Bank", "EMI"],
};
